import {
  useEffect,
  useMemo,
  useState,
  useCallback,
  lazy,
  Suspense,
} from "react";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import {
  useLandingPackagesQuery,
  useLandingPageSettingsQuery,
} from "./lib/queries";

// Section components
import Hero from "./components/sections/Hero";
import BrandShowcase from "./components/sections/BrandShowcase";
import BookingCTA from "./components/sections/BookingCTA";
import WhyChooseUs from "./components/sections/WhyChooseUs";
import HowItWorks from "./components/sections/HowItWorks";
import Stats from "./components/sections/Stats";
import QuickBookingSection from "./components/sections/QuickBookingSection";
import OrderTrackingSection from "./components/sections/OrderTrackingSection";
import LuxuryGallery from "./components/sections/LuxuryGallery";
import KnowledgeSection from "./components/sections/KnowledgeSection";

import FAQ from "./components/sections/FAQ";
const Location = lazy(() => import("./components/sections/Location"));
import PricingPackages from "./components/sections/PricingPackages";
import BeforeAfterGallery from "./components/sections/BeforeAfterGallery";
import AboutUs from "./components/sections/AboutUs";
import Footer from "./components/layout/Footer";
import Header from "./components/layout/Header";
const BookingWizard = lazy(() => import("./components/booking/BookingWizard"));
import Reveal from "./components/ui/Reveal";

// Extracted UI components and hooks
import { ScrollProgressBar } from "./components/ui/ScrollProgressBar";
import { FilmGrainOverlay } from "./components/ui/FilmGrainOverlay";
import { Toast } from "./components/ui/Toast";
import { useBookingToasts } from "./hooks/useBookingToasts";
import SpeedDial from "./components/ui/SpeedDial";
import Preloader from "./components/ui/Preloader";
import { useMobilePerformance } from "./hooks/useMobilePerformance";

type Toast = { id: number; title: string; message?: string } | null;

type UiPackage = {
  id: number;
  name: string;
  description?: string | null;
  price: number;
  badgeLabel: string | null;
  deliveryConfig: {
    enabled: boolean;
    allowPickup: boolean;
    pickupFee?: number | null;
    allowDropoff: boolean;
    dropoffFee?: number | null;
  };
  serviceId?: number | null;
  features: string[];
};

function App(): JSX.Element {
  const [isBookingOpen, setIsBookingOpen] = useState<boolean>(false);
  const [initialPackage, setInitialPackage] = useState<UiPackage | null>(null);
  const [isBookingChunkLoading, setIsBookingChunkLoading] =
    useState<boolean>(false);
  const [toast, setToast] = useState<Toast>(null);
  const location = useLocation();
  const { isMobile } = useMobilePerformance();

  const bookingWizardImportRef = useMemo(
    () => ({ promise: null as Promise<any> | null }),
    []
  );

  const prefetchBookingWizard = useCallback(() => {
    if (bookingWizardImportRef.promise) return bookingWizardImportRef.promise;
    bookingWizardImportRef.promise = import("./components/booking/BookingWizard");
    return bookingWizardImportRef.promise;
  }, [bookingWizardImportRef]);

  useEffect(() => {
    const handleFirstInteraction = () => {
      prefetchBookingWizard();
    };

    window.addEventListener("touchstart", handleFirstInteraction, {
      passive: true,
      once: true,
    });
    window.addEventListener("mousemove", handleFirstInteraction, {
      passive: true,
      once: true,
    });

    return () => {
      window.removeEventListener("touchstart", handleFirstInteraction);
      window.removeEventListener("mousemove", handleFirstInteraction);
    };
  }, [prefetchBookingWizard]);

  const openBooking = (
    options: { package?: UiPackage | null; service?: any } = {}
  ) => {
    const pkg = options.package ?? null;
    setInitialPackage(pkg);

    // Start loading the wizard chunk immediately so mobile doesn't feel stuck.
    setIsBookingChunkLoading(true);
    prefetchBookingWizard().finally(() => setIsBookingChunkLoading(false));

    setIsBookingOpen(true);
  };

  const {
    data: packagesData,
    isLoading: packagesLoading,
    error: packagesErrorObj,
    refetch: refetchPackages,
  } = useLandingPackagesQuery();

  const { data: landingSettings } = useLandingPageSettingsQuery();
  const showHero = landingSettings?.show_hero ?? true;
  const showBrands = landingSettings?.show_brands ?? true;
  const showPackages = landingSettings?.show_packages ?? true;
  const showBeforeAfter = landingSettings?.show_before_after ?? true;
  const showWhyUs = landingSettings?.show_why_us ?? true;
  const showHowItWorks = landingSettings?.show_how_it_works ?? true;
  const showStats = landingSettings?.show_stats ?? true;
  const showBookingCta = landingSettings?.show_booking_cta ?? true;
  const showLocation = landingSettings?.show_location ?? true;
  const showFaq = landingSettings?.show_faq ?? true;
  const showAbout = landingSettings?.show_about ?? true;
  const showQuickBooking = true; // Hardcoded for now
  const showOrderTracking = true; // Hardcoded for now
  const showLuxuryGallery = true; // New lux gallery section
  const showKnowledge = true; // Section 0: Education section

  const packagesError = packagesErrorObj ? "تعذر تحميل الباقات حالياً" : null;
  const packages = useMemo<UiPackage[]>(() => {
    return (packagesData ?? []).map((pkg: any) => ({
      id: pkg.id,
      name: pkg.name_ar,
      description: pkg.short_description_ar ?? null,
      price: pkg.price,
      badgeLabel: pkg.badge_enabled ? pkg.badge_label_ar ?? null : null,
      deliveryConfig: {
        enabled: pkg.delivery_enabled,
        allowPickup: pkg.allow_pickup,
        pickupFee: pkg.pickup_fee,
        allowDropoff: pkg.allow_dropoff,
        dropoffFee: pkg.dropoff_fee,
      },
      serviceId: pkg.service_id,
      features: pkg.features_ar ?? [],
    }));
  }, [packagesData]);

  // Handle scroll-to-section navigation
  useEffect(() => {
    const target =
      (location.state as any)?.scrollTo ||
      (location.hash ? location.hash.substring(1) : null);

    if (target) {
      const scrollToTarget = () => {
        const el = document.getElementById(target);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      };
      requestAnimationFrame(scrollToTarget);

      // Clear the scrollTo state to avoid re-scrolling when navigating back
      const historyState = window.history.state;
      if (historyState && historyState.usr && historyState.usr.scrollTo) {
        const newState = { ...historyState, usr: { ...historyState.usr } };
        delete newState.usr.scrollTo;
        window.history.replaceState(newState, "");
      }
    }
  }, [location]);

  // Toast display logic
  const showToast = useCallback((title: string, message?: string) => {
    const id = Date.now();
    setToast({ id, title, message });
    if (import.meta.env.DEV) {
      console.log("[toast] show", title, message);
    }
    setTimeout(() => {
      setToast((current) => (current?.id === id ? null : current));
    }, 4000);
  }, []);

  // Track booking status updates with toasts
  useBookingToasts(showToast);

  return (
    <div className="min-h-screen bg-background-light dark:bg-background-dark text-text-primary-light dark:text-text-primary-dark transition-colors duration-300 relative selection:bg-primary/30 overflow-x-hidden w-full">
      {/* Global Scroll Progress Bar */}
      <ScrollProgressBar />

      <Preloader />
      
      {/* Global Cinematic Atmosphere - DESKTOP ONLY for performance */}
      {!isMobile && (
        <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
          {/* Deep Radiant Core */}
          <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-primary/10 blur-[120px] rounded-full animate-pulse" />
          <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-red-900/10 blur-[150px] rounded-full" />
          
          {/* Floating Magma Orbs */}
          <motion.div 
            initial={{ x: 0, y: 0 }}
            animate={{ x: [0, 100, 0], y: [0, 50, 0] }}
            transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
            className="absolute top-[30%] right-[10%] w-64 h-64 bg-primary/5 blur-[80px] rounded-full" 
          />
          <motion.div 
            initial={{ x: 0, y: 0 }}
            animate={{ x: [0, -60, 0], y: [0, 80, 0] }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            className="absolute bottom-[20%] left-[5%] w-96 h-96 bg-red-600/5 blur-[100px] rounded-full" 
          />

          {/* Cinematic Embers */}
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0, x: 0, y: 0 }}
              animate={{ 
                opacity: [0, 0.4, 0],
                scale: [0, 1, 0],
                x: [0, (Math.random() - 0.5) * 200],
                y: [0, -800]
              }}
              transition={{ 
                duration: 12 + Math.random() * 10,
                repeat: Infinity,
                delay: Math.random() * 5,
                ease: "linear"
              }}
              className="absolute bottom-0 w-1 h-1 bg-primary rounded-full blur-[1px] shadow-[0_0_8px_rgba(220,20,60,0.8)]"
              style={{ left: `${10 + Math.random() * 80}%` }}
            />
          ))}

          {/* Global Grid Overlay (Subtle) */}
          <div className="absolute inset-0 bg-[url('/grid.svg')] bg-[length:40px_40px] opacity-[0.02] mix-blend-overlay" />
        </div>
      )}

      {/* Cinematic Film Grain Overlay - Desktop only */}
      {!isMobile && <FilmGrainOverlay />}

      <Header onOpenBooking={() => openBooking()} />
      <main>
        {showHero && <Hero onOpenBooking={() => openBooking()} />}

        {showKnowledge && (
          <Reveal>
            <KnowledgeSection />
          </Reveal>
        )}

        {showPackages && (
          <Reveal>
            <PricingPackages
              packages={packages}
              loading={packagesLoading}
              error={packagesError}
              onRetry={() => refetchPackages()}
              onOpenBooking={openBooking}
            />
          </Reveal>
        )}

        {showQuickBooking && (
          <Reveal>
            <QuickBookingSection onOpenBooking={() => openBooking()} />
          </Reveal>
        )}

        {showLuxuryGallery && (
          <Reveal>
            <LuxuryGallery />
          </Reveal>
        )}

        {showBeforeAfter && (
          <Reveal>
            <BeforeAfterGallery />
          </Reveal>
        )}

        {showBrands && (
          <Reveal>
            <BrandShowcase />
          </Reveal>
        )}

        {showAbout && (
          <Reveal>
            <AboutUs />
          </Reveal>
        )}

        {showWhyUs && (
          <Reveal>
            <WhyChooseUs />
          </Reveal>
        )}

        {showStats && (
          <Reveal>
            <Stats />
          </Reveal>
        )}

        {showHowItWorks && (
          <Reveal>
            <HowItWorks />
          </Reveal>
        )}

        {showOrderTracking && (
          <Reveal>
            <OrderTrackingSection />
          </Reveal>
        )}

        {showBookingCta && (
          <Reveal>
            <BookingCTA onOpenBooking={() => openBooking()} packages={packages} />
          </Reveal>
        )}

        {showLocation && (
          <Reveal>
            <Suspense fallback={<div className="h-96 bg-[#0a0a0a] animate-pulse rounded-2xl" />}>
              <Location />
            </Suspense>
          </Reveal>
        )}

        {showFaq && (
          <Reveal>
            <FAQ />
          </Reveal>
        )}
      </main>
      <Footer />
      <Suspense
        fallback={
          isBookingOpen ? (
            <div className="fixed inset-0 z-[100] bg-black/70 flex items-end sm:items-center justify-center sm:p-4">
              <div className="w-full max-w-3xl bg-[#0a0a0a]/95 rounded-t-[32px] sm:rounded-3xl border-t sm:border border-white/10 shadow-[0_0_50px_rgba(0,0,0,0.5)] flex flex-col max-h-[92dvh] sm:h-[700px] sm:max-h-[90vh]">
                <div className="flex-1 flex items-center justify-center">
                  <div className="w-10 h-10 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                </div>
              </div>
            </div>
          ) : null
        }
      >
        <BookingWizard
          isOpen={isBookingOpen}
          onClose={() => setIsBookingOpen(false)}
          initialPackage={initialPackage}
          packages={packages}
          packagesLoading={packagesLoading}
          packagesError={packagesError}
          onRetryPackages={() => refetchPackages()}
        />
      </Suspense>

      {isBookingOpen && isBookingChunkLoading && (
        <div className="fixed inset-0 z-[99] bg-black/0" />
      )}

      {/* Mobile scroll-to-top */}
      {/* Scroll to Top is now integrated into SpeedDial */}
      {/* <ScrollToTopButton isBookingOpen={isBookingOpen} /> */}

      {/* Mobile Speed Dial Menu */}
      <SpeedDial 
        onOpenBooking={() => openBooking()} 
        isBookingOpen={isBookingOpen}
      />

      {/* Toast */}
      <Toast toast={toast} />
    </div>
  );
}

export default App;
